import moment from 'moment';
import {
  DashboardDateRange,
  DashboardFilters,
  DashboardPeriod,
  IsoDate,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const COMPANY_TIME_ZONE = 'America/New_York';
const LABEL_SEPARATOR = ' · ';
const RANGE_SEPARATOR = ' – ';

const companyDayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: COMPANY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const companyDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: COMPANY_TIME_ZONE,
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
});

function parseIsoDate(value: IsoDate | null | undefined): moment.Moment | null {
  if (typeof value !== 'string') {
    return null;
  }
  const parsed = moment(value, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

/** Calendar day in company (Eastern) time for the given instant. */
export function companyToday(now: Date): IsoDate {
  const parts = companyDayFormatter.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** `Sep 28, 2026 3:16 PM`: the instant in company (Eastern) time, no zone; built from parts, never the locale's own. */
export function formatCompanyDateTime(instant: Date): string {
  const parts = companyDateTimeFormatter.formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('month')} ${part('day')}, ${part('year')} ${part('hour')}:${part('minute')} ${part('dayPeriod')}`;
}

/** Resolves the preset (or custom pair) to an inclusive range; `null` when a custom side is missing. */
export function resolveDateRange(filters: DashboardFilters, today: IsoDate): DashboardDateRange | null {
  if (filters.period === DashboardPeriod.CUSTOM) {
    return filters.customFrom && filters.customTo ? { from: filters.customFrom, to: filters.customTo } : null;
  }
  const todayMoment = parseIsoDate(today);
  if (!todayMoment) {
    return null;
  }
  const unit: moment.unitOfTime.StartOf =
    filters.period === DashboardPeriod.MTD ? 'month' : filters.period === DashboardPeriod.QTD ? 'quarter' : 'year';
  return { from: todayMoment.clone().startOf(unit).format(ISO_DATE_FORMAT), to: today };
}

/** Strict `YYYY-MM-DD` on every side and `from <= to <= today`. */
export function isValidCustomRange(from: IsoDate | null, to: IsoDate | null, today: IsoDate): boolean {
  const fromMoment = parseIsoDate(from);
  const toMoment = parseIsoDate(to);
  const todayMoment = parseIsoDate(today);
  if (!fromMoment || !toMoment || !todayMoment) {
    return false;
  }
  return fromMoment.isSameOrBefore(toMoment, 'day') && toMoment.isSameOrBefore(todayMoment, 'day');
}

/** Moment or Date → local calendar day; anything else (including `""` and invalid values) → `null`. */
export function toIsoDate(value: unknown): IsoDate | null {
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format(ISO_DATE_FORMAT) : null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : moment(value).format(ISO_DATE_FORMAT);
  }
  return null;
}

/** `YYYY-MM-DD` → local midnight of that calendar day. */
export function fromIsoDate(iso: IsoDate): Date {
  return moment(iso, ISO_DATE_FORMAT, true).toDate();
}

/** `Jul 1 – Jul 27, 2026` within one year, `Dec 15, 2025 – Jan 15, 2026` across years. */
export function formatDateRange(range: DashboardDateRange | null): string | null {
  const from = parseIsoDate(range?.from);
  const to = parseIsoDate(range?.to);
  if (!from || !to) {
    return null;
  }
  if (from.year() === to.year()) {
    return `${from.format('MMM D')}${RANGE_SEPARATOR}${to.format('MMM D, YYYY')}`;
  }
  return `${from.format('MMM D, YYYY')}${RANGE_SEPARATOR}${to.format('MMM D, YYYY')}`;
}

/** Joins the non-empty parts with ` · `; empty string when both are missing. */
export function formatFreshnessLabel(updated: string | null, range: string | null): string {
  return [updated, range].filter((part) => !!part).join(LABEL_SEPARATOR);
}
